import type { CodingAnswerOption } from '@seen/core/codings'
import { type JSX, type ReactNode, useCallback } from 'react'
import { ComboboxAsync } from '#src/components/core/combobox/ComboboxAsync.tsx'
import { useReportPopoverOpen } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import type { ValueSetAutocompleteValue } from '#src/components/ValueSetAutocomplete/toOptions.ts'
import { valueSetAutocompleteLoadOptions } from '#src/components/ValueSetAutocomplete/valueSetAutocompleteLoadOptions.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

// Picks a coded answer by searching the value set it is bound to, for
// answer lists too long to carry inline. The answer keeps its system.
export function ValueSetAnswerPicker({
  answerValueSet,
  onSelect,
  children,
  initialOpen = false,
}: {
  answerValueSet: string
  onSelect: (answer: CodingAnswerOption) => void
  children: ReactNode
  initialOpen?: boolean
}): JSX.Element {
  const medplum = useMedplum()
  const reportOpen = useReportPopoverOpen(initialOpen)

  const loadOptions = useCallback(
    ({ input, signal }: { input: string; signal: AbortSignal }) =>
      valueSetAutocompleteLoadOptions({
        input,
        signal,
        medplum,
        binding: answerValueSet,
      }),
    [medplum, answerValueSet]
  )

  return (
    <ComboboxAsync<ValueSetAutocompleteValue>
      queryKey={['observation-answer-value-set', answerValueSet]}
      loadOptions={loadOptions}
      getValue={(value) => `${value.system}|${value.code}`}
      onSelect={(value) => {
        if (value) {
          onSelect({
            system: value.system,
            code: value.code,
            display: value.display,
          })
        }
      }}
      searchPlaceholder="Search answers…"
      rankOnly
      initialOpen={initialOpen}
      onOpenChange={reportOpen}
      modal={false}
    >
      {children}
    </ComboboxAsync>
  )
}
